import { Calendar, Clock, Users } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BaseNavigationBar, SettingsBackButton } from "@/lib/components/navigation";
import { PageContent } from "@/lib/components/page-content";
import { SettingsSearchTrigger } from "../../search/components/settings-search-trigger";
import { ShiftCalendarTab } from "./components/shift-calendar-tab";
import { ShiftTemplateTab } from "./components/shift-template-tab";
import { WorkerPlansTab } from "./components/worker-plans-tab";

type TabId = "templates" | "plans" | "calendar";

interface Tab {
  id: TabId;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
}

// This will be converted to use translations

export function ShiftManagementPage() {
  const { t } = useTranslation("settings");
  const [activeTab, setActiveTab] = useState<TabId>("templates");

  const tabs: Tab[] = [
    {
      id: "templates",
      label: "Shift Templates",
      icon: Clock,
      description: "Create reusable shift patterns with time intervals and break rules",
    },
    {
      id: "plans",
      label: "Worker Plans",
      icon: Users,
      description: "Assign templates to workers or create custom schedules",
    },
    {
      id: "calendar",
      label: "Shift Calendar",
      icon: Calendar,
      description: "View and manage daily shift schedules in calendar format",
    },
  ];

  return (
    <>
      <BaseNavigationBar
        title={t("sections.shiftManagement.title")}
        subtitle={t("sections.shiftManagement.description")}
        leadElement={<SettingsBackButton key="back-button" />}
        trailingElements={[<SettingsSearchTrigger key="search-trigger" />]}
      />

      <PageContent>
        <div className="space-y-6">
          {/* Tab Navigation */}
          <div className="border-b border-border">
            <div className="flex space-x-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    type="button"
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`group flex items-center gap-3 pb-4 border-b-2 transition-colors ${
                      activeTab === tab.id
                        ? "border-primary text-primary"
                        : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <div className="text-left">
                      <div className="font-medium">{tab.label}</div>
                      <div className="text-xs opacity-75 max-w-48">{tab.description}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tab Content */}
          <div className="min-h-[600px]">
            {activeTab === "templates" && <ShiftTemplateTab />}
            {activeTab === "plans" && <WorkerPlansTab />}
            {activeTab === "calendar" && <ShiftCalendarTab />}
          </div>
        </div>
      </PageContent>
    </>
  );
}
